// components/LoginForm.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useSupabaseFromContext } from './SupabaseClientContext';
import { useRouter } from 'next/router';
import { Form, Input, Button, Alert, Typography, Spin, Divider } from 'antd';
import { MailOutlined, LockOutlined, LogoutOutlined, KeyOutlined, SafetyOutlined } from '@ant-design/icons';
import { Session, User } from '@supabase/supabase-js';
import { createPasskeyAuth } from 'supakeys';

const { Title, Text } = Typography;

interface LoginFormValues {
  email: string;
  password?: string;
  otp?: string;
  newPassword?: string;
  confirmPassword?: string;
}

interface LoginFormProps {
  defaultEmail?: string;
  defaultPassword?: string;
  successRedirectUrl?: string;
  onSubmitSuccess?: (session: Session) => void;
  onSubmitError?: (errorMessage: string) => void;
  previewAsLoggedIn?: boolean;
}

export function LoginForm({
  defaultEmail = '',
  defaultPassword = '',
  successRedirectUrl,
  onSubmitSuccess,
  onSubmitError,
  previewAsLoggedIn,
}: LoginFormProps) {
  const supabaseClient = useSupabaseFromContext();
  const router = useRouter();
  const [form] = Form.useForm();

  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Modes
  const [forgotMode, setForgotMode] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  
  // NEW: Biometric enrollment prompt after login
  const [biometricPromptMode, setBiometricPromptMode] = useState(false);

  // Initialize Supakeys Client
  const passkeys = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return createPasskeyAuth(supabaseClient, {
      rpId: window.location.hostname,
      rpName: 'My App', 
    });
  }, [supabaseClient]);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION synchronously on mount,
    // giving us the current user without a separate getUser() call.
    // Running both in parallel caused the "lock was released" race condition.
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [supabaseClient]);

  const proceedToApp = () => {
    const redirectTo = successRedirectUrl || (router.query.redirect_to as string) || '/account';
    router.push(redirectTo);
  };

  // --- PASSKEY HANDLERS ---
  const handleLoginWithPasskey = async () => {
    if (!passkeys) return;
    setLoading(true);
    setError(null);
    try {
      const email = form.getFieldValue('email');
      const { success, session, error: pkError } = await passkeys.signIn(email ? { email } : undefined);
      
      if (!success) throw new Error(pkError?.message || 'Biometric login failed.');
      
      if (session) {
        onSubmitSuccess?.(session);
        proceedToApp();
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterBiometrics = async () => {
    if (!passkeys) return;
    setLoading(true);
    setError(null);
    try {
      const { data: { user: currentUser } } = await supabaseClient.auth.getUser();
      if (!currentUser?.email) throw new Error("No user email found.");

      const { success, error: pkError } = await passkeys.register({
        email: currentUser.email,
        authenticatorName: 'Windows Hello / Biometric'
      });

      if (!success) throw new Error(pkError?.message || 'Failed to save biometrics.');

      setSuccessMsg('Biometrics saved successfully!');
      setTimeout(() => proceedToApp(), 1500);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: LoginFormValues) => {
    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (forgotMode && !otpMode) {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const { error } = await supabaseClient.auth.resetPasswordForEmail(values.email, {
          redirectTo: `${origin}/account`,
        });
        if (error) throw error;
        setSuccessMsg('Reset email sent. Enter the code from your email.');
        setOtpMode(true);
      }
      else if (otpMode && !recoveryMode) {
        const { data, error } = await supabaseClient.auth.verifyOtp({
          email: values.email,
          token: values.otp!,
          type: 'recovery',
        });
        if (error) throw error;
        setSuccessMsg('Code verified. Please set a new password.');
        setRecoveryMode(true);
      }
      else if (recoveryMode) {
        if (values.newPassword !== values.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        const { error } = await supabaseClient.auth.updateUser({
          password: values.newPassword!,
        });
        if (error) throw error;
        setSuccessMsg('Password updated successfully.');
        setForgotMode(false);
        setOtpMode(false);
        setRecoveryMode(false);
        form.resetFields();
      }
      else {
        // Normal password login
        const { error: signInError, data: signInData } =
          await supabaseClient.auth.signInWithPassword({
            email: values.email,
            password: values.password || '',
          });
        if (signInError) throw signInError;

        if (signInData.session) {
          onSubmitSuccess?.(signInData.session);

          // CHECK FOR BIOMETRIC SUPPORT
          if (passkeys) {
            const support = await passkeys.isSupported();
            if (support.webauthn && support.platformAuthenticator) {
              const { success, passkeys: existingKeys } = await passkeys.listPasskeys();
              // If user has 0 passkeys registered, ask them to save biometrics
              if (success && existingKeys && existingKeys.length === 0) {
                setBiometricPromptMode(true);
                return;
              }
            }
          }
          proceedToApp();
        }
      }
    } catch (e: any) {
      setError(e.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await supabaseClient.auth.signOut();
    } catch (e: any) {
      setError('Logout failed.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return <Spin />;

  // MODE: Reset Password
  if (recoveryMode) {
    return (
      <div style={{ maxWidth: 400, margin: 'auto', padding: '20px' }}>
        <Title level={3} style={{ textAlign: 'center', marginBottom: '24px' }}>Reset Password</Title>
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item name="newPassword" label="New Password" rules={[{ required: true }]}>
            <Input.Password placeholder="New password" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="Confirm Password" rules={[{ required: true }]}>
            <Input.Password placeholder="Confirm password" />
          </Form.Item>
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
          <Button type="primary" htmlType="submit" loading={loading} block>Update Password</Button>
        </Form>
      </div>
    );
  }

  // MODE: Post-login enrollment prompt
  if (biometricPromptMode) {
    return (
      <div style={{ maxWidth: 400, margin: 'auto', padding: '20px', textAlign: 'center' }}>
        <SafetyOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
        <Title level={4}>Login Successful!</Title>
        <Text style={{ display: 'block', marginBottom: '24px' }}>
          Would you like to use Windows Hello or a Pin to sign in faster next time?
        </Text>
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        {successMsg && <Alert message={successMsg} type="success" showIcon style={{ marginBottom: 16 }} />}
        <Button type="primary" onClick={handleRegisterBiometrics} loading={loading} block style={{ marginBottom: 12 }}>
          Enable Pin / Biometric Login
        </Button>
        <Button type="default" onClick={proceedToApp} disabled={loading} block>Skip for now</Button>
      </div>
    );
  }

  // MODE: Logged In (Show Logout)
  if (user) {
    return (
      <div style={{ maxWidth: 400, margin: 'auto', padding: '20px', textAlign: 'center' }}>
        <Title level={4}>You are logged in.</Title>
        <Text type="secondary" style={{ marginBottom: '24px', display: 'block' }}>{user.email}</Text>
        <Button type="primary" danger icon={<LogoutOutlined />} onClick={handleLogout} loading={loading} block>Log Out</Button>
      </div>
    );
  }

  // DEFAULT: Login Form
  return (
    <div style={{ maxWidth: 400, margin: 'auto', padding: '20px' }}>
      <Title level={3} style={{ textAlign: 'center', marginBottom: '24px' }}>
        {otpMode ? 'Enter Code' : forgotMode ? 'Forgot Password' : 'Login'}
      </Title>

      {!otpMode && !forgotMode && (
        <>
          <Button icon={<KeyOutlined />} onClick={handleLoginWithPasskey} size="large" block style={{ marginBottom: '16px' }}>
            Sign in with Pin / Biometrics
          </Button>
          <Divider plain>OR CONTINUE WITH EMAIL</Divider>
        </>
      )}

      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
          <Input prefix={<MailOutlined />} placeholder="Email" />
        </Form.Item>

        {!forgotMode && !otpMode && (
          <Form.Item name="password" label="Password" rules={[{ required: true }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Password" />
          </Form.Item>
        )}

        {otpMode && (
          <Form.Item name="otp" label="OTP Code" rules={[{ required: true }]}>
            <Input placeholder="6-digit code" />
          </Form.Item>
        )}

        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        {successMsg && <Alert message={successMsg} type="success" showIcon style={{ marginBottom: 16 }} />}

        <Button type="primary" htmlType="submit" loading={loading} block>
          {otpMode ? 'Verify Code' : forgotMode ? 'Send Reset Link' : 'Log in'}
        </Button>

        {!otpMode && !forgotMode && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <a onClick={() => setForgotMode(true)}>Forgot password?</a>
          </div>
        )}
        
        {(forgotMode || otpMode) && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <a onClick={() => { setForgotMode(false); setOtpMode(false); }}>Back to login</a>
          </div>
        )}
      </Form>
    </div>
  );
}
